/* eslint-disable sonarjs/no-duplicate-string */
import React from 'react';
import { Legend } from '../../../components/fieldset';

export const reactSelectReset = {
  control: (provided, state) => ({
    ...provided,
    borderColor: 'rgba(24, 24, 27, 0.1)',
    borderRadius: '8px',
    minHeight: '44px',
    height: '44px',
    padding: '0',
    backgroundColor: '#fff',
    boxShadow: state.isFocused
      ? `0 0 0 2px var(--color-brand-green-1)`
      : state.isDisabled
        ? 'none'
        : '0 1px 2px rgba(0, 0, 0, 0.1)',
    transition: 'box-shadow 0.2s ease-in-out',
    '@media (min-width: 640px)': {
      minHeight: '37px',
      height: '37px',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    '@media (min-width: 640px)': {
      height: '37px',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.375rem',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
  }),
  option: (provided, state) => ({
    ...provided,
    padding: '0.5rem 1rem',
    backgroundColor: state.isSelected
      ? 'var(--color-brand-green-1)'
      : state.isFocused
        ? 'var(--color-brand-green-0)'
        : 'transparent',
    color:
      state.isSelected || state.isFocused
        ? 'var(--color-brand-white-1)'
        : 'var(--color-brand-black-0)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--color-brand-green-1)',
      color: 'var(--color-brand-white-1)',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: '#333333',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--color-brand-black-0)',
  }),
  multiValue: (provided) => ({
    ...provided,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
  }),
};

// Shared components
export const BrandLegend = ({ children }: { children: React.ReactNode }) => (
  <Legend className="tw:text-lg tw:font-semibold">
    <span className="tw:text-brand-green-0 tw:dark:text-zinc-100">{children}</span>
  </Legend>
);

type ValidationMessageProps = {
  message?: string | null;
  icon?: React.ReactNode;
  colorClass?: string;
};

export const ValidationMessage = ({ message, icon, colorClass }: ValidationMessageProps) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

/** True when any field-level Missing Info / validation row is shown (same object passed to ValidationMessage). */
export function onboardingStepHasVisibleWarnings(
  validationMessages: Record<string, unknown>
): boolean {
  return Object.keys(validationMessages).length > 0;
}

type StepHeaderStatusBannerProps = {
  hasWarnings: boolean;
  showComplete: boolean;
};

export const StepHeaderStatusBanner = ({
  hasWarnings,
  showComplete,
}: StepHeaderStatusBannerProps) => {
  if (hasWarnings) {
    return (
      <div
        className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-stoplight-danger/40 tw:bg-stoplight-danger/10 tw:px-3 tw:py-2 tw:text-sm tw:text-stoplight-danger tw:dark:border-red-500/40 tw:dark:bg-red-950/30 tw:dark:text-red-200"
        role="status"
      >
        <i
          className="fa-duotone fa-solid fa-circle-three-quarters tw:text-base tw:leading-none"
          aria-hidden
        />
        <span>Almost there! Please supply missing information as noted below</span>
      </div>
    );
  }
  if (showComplete) {
    return (
      <div
        className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-stoplight-success/40 tw:bg-stoplight-success/10 tw:px-3 tw:py-2 tw:text-sm tw:text-stoplight-success tw:dark:border-emerald-500/40 tw:dark:bg-emerald-950/30 tw:dark:text-emerald-200"
        role="status"
      >
        <i className="fas fa-check tw:text-base tw:leading-none" aria-hidden />
        <span>Everything&apos;s complete here!</span>
      </div>
    );
  }
  return null;
};
